import React, { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2, X, Save } from "lucide-react";
import Swal from "sweetalert2";
import { Department } from "../types";
import {
  createDepartment,
  deleteDepartment,
  getDepartments,
  updateDepartment,
} from "../lib/api";

const emptyForm = { name: "", label: "", description: "" };

export const Departments: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  async function fetchDepartments() {
    setLoading(true);
    try {
      setDepartments(await getDepartments());
    } catch (err) {
      console.error("Gagal memuat departemen:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDepartments();
  }, []);

  function startEdit(dept: Department) {
    setEditingId(dept.id);
    setForm({
      name: dept.name,
      label: dept.label,
      description: dept.description || "",
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        label: form.label.trim(),
        description: form.description.trim() || undefined,
      };

      if (editingId === null) {
        await createDepartment(payload);
      } else {
        await updateDepartment(editingId, payload);
      }

      await Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text:
          editingId === null
            ? "Departemen berhasil ditambahkan"
            : "Departemen berhasil diperbarui",
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      cancelEdit();
      fetchDepartments();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      Swal.fire({
        icon: "error",
        title: "Gagal!",
        text: message.replace(/^Error:\s*/, ""),
        confirmButtonText: "Tutup",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(dept: Department) {
    const result = await Swal.fire({
      title: "Hapus departemen?",
      text: `Departemen "${dept.label}" akan dihapus.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDepartment(dept.id);
      if (editingId === dept.id) cancelEdit();
      fetchDepartments();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      Swal.fire({
        icon: "error",
        title: "Gagal!",
        text: message.replace(/^Error:\s*/, ""),
        confirmButtonText: "Tutup",
      });
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 lg:pt-0">
      <div className="p-4 lg:pr-14 lg:pb-4 sm:pt-6 lg:pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Manajemen Departemen
          </h1>
          <p className="text-gray-600">
            Tambah dan kelola departemen. Hanya admin yang dapat mengakses
            halaman ini.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-blue-600" />
              {editingId === null
                ? "Tambah Departemen Baru"
                : "Edit Departemen"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Departemen
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="contoh: Produksi"
                  className="w-full rounded-lg border-gray-300 border px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label
                </label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="contoh: Produksi"
                  className="w-full rounded-lg border-gray-300 border px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deskripsi
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  rows={3}
                  placeholder="Opsional"
                  className="w-full rounded-lg border-gray-300 border px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors font-medium"
                >
                  {editingId === null ? (
                    <>
                      <Plus className="h-4 w-4 mr-2" /> Tambah Departemen
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" /> Simpan
                    </>
                  )}
                </button>
                {editingId !== null && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="flex items-center justify-center border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X className="h-4 w-4 mr-2" /> Batal
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Daftar Departemen
            </h3>

            {loading ? (
              <p className="text-sm text-gray-500">Memuat...</p>
            ) : departments.length === 0 ? (
              <p className="text-sm text-gray-500">Belum ada departemen.</p>
            ) : (
              <div className="space-y-2">
                {departments.map((dept) => (
                  <div
                    key={dept.id}
                    className="flex items-start justify-between border border-gray-200 rounded-lg p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {dept.label}
                        <span className="ml-2 text-xs text-gray-500">
                          {dept.name}
                        </span>
                      </p>
                      {dept.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {dept.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {dept.employee_count ?? 0} karyawan
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-3">
                      <button
                        onClick={() => startEdit(dept)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit departemen"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(dept)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Hapus departemen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Departments;
